import React, { useState, useEffect, useCallback } from 'react';
import { Headset, RefreshCw, MessageSquare, Inbox, CheckCircle2, Users, Send, Eye, EyeOff } from 'lucide-react';
import { api, timeAgo } from '../api';

const AGENT_COLORS = ['#0EA5E9', '#F59E0B', '#10B981', '#8B5CF6', '#F43F5E', '#14B8A6', '#F97316', '#6366F1'];

const KPI = ({ icon: Icon, label, value, tint }) => (
  <div className={`flex-1 min-w-[120px] rounded-xl border-2 px-3 py-2.5 ${tint}`}>
    <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex items-center gap-1"><Icon size={11} /> {label}</p>
    <p className="font-display font-extrabold text-2xl mt-0.5">{(value ?? 0).toLocaleString('en-IN')}</p>
  </div>
);

export default function DeskAnalytics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState([]);
  const [showHidden, setShowHidden] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/desk-hidden').then((r) => setHidden(r.data.names)).catch(() => {});
    api.get(`/desk-analytics?days=${days}`)
      .then((r) => { setData(r.data); setErr(''); })
      .catch((e) => setErr(e.response?.data?.detail || 'Desk analytics is not available yet'))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const toggleHidden = async (name) => {
    const next = hidden.includes(name) ? hidden.filter((n) => n !== name) : [...hidden, name];
    setHidden(next);
    await api.put('/desk-hidden', { names: next });
  };

  const visibleAgents = (data?.agents || []).filter((a) => showHidden || !hidden.includes(a.name));
  const agentNames = data?.daily_chart?.length
    ? [...new Set(data.daily_chart.flatMap((d) => Object.keys(d).filter((k) => k !== 'day')))].filter((n) => !hidden.includes(n))
    : [];
  const dayLabel = (day) => (/^\d{4}-\d{2}-/.test(day) ? day.slice(5) : day);
  const maxDay = data?.daily_chart?.length
    ? Math.max(1, ...data.daily_chart.map((d) => agentNames.reduce((s, n) => s + (d[n] || 0), 0)))
    : 1;

  return (
    <div data-testid="desk-analytics">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h3 className="font-display font-bold text-lg text-slate-800 flex items-center gap-2"><Headset size={17} className="text-sky-600" /> Connect Desk Analytics</h3>
        <div className="flex bg-slate-100 border-2 border-slate-200 rounded-full p-0.5">
          {[7, 14, 30].map((d) => (
            <button key={d} data-testid={`desk-days-${d}`} onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${days === d ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
              {d}d
            </button>
          ))}
        </div>
        <button data-testid="desk-analytics-refresh" onClick={load} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500" title="Refresh (server caches 5 min)">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
        {data?.generated_at && <span className="text-[10px] font-semibold text-slate-400">updated {timeAgo(data.generated_at)}</span>}
      </div>

      {err && !data ? (
        <div className="bg-sky-50 border-2 border-sky-200 rounded-xl p-6 text-center" data-testid="desk-analytics-unavailable">
          <p className="font-hand text-xl text-sky-800">📡 {err}</p>
          <p className="text-[11px] text-sky-600 mt-1">The Desk app's partner analytics endpoint hasn't gone live yet — this card will light up automatically once it does.</p>
        </div>
      ) : data && (
        <div className="bg-white border-2 border-slate-200 rounded-xl sticky-shadow p-4 space-y-4">
          <div className="flex gap-2.5 flex-wrap" data-testid="desk-kpis">
            <KPI icon={Inbox} label="Open" value={data.overview?.open_conversations} tint="bg-amber-50 border-amber-200 text-amber-900" />
            <KPI icon={CheckCircle2} label="Resolved" value={data.overview?.resolved_conversations} tint="bg-emerald-50 border-emerald-200 text-emerald-900" />
            <KPI icon={MessageSquare} label="Messages In" value={data.overview?.messages_in} tint="bg-sky-50 border-sky-200 text-sky-900" />
            <KPI icon={Send} label="Messages Out" value={data.overview?.messages_out} tint="bg-indigo-50 border-indigo-200 text-indigo-900" />
            <KPI icon={Users} label="Contacts" value={data.overview?.contacts} tint="bg-slate-50 border-slate-200 text-slate-800" />
          </div>

          {data.agents?.length > 0 && (
            <div className="overflow-x-auto scroll-thin" data-testid="desk-agent-table">
              {hidden.length > 0 && (
                <button data-testid="desk-show-hidden" onClick={() => setShowHidden(!showHidden)}
                  className="text-[10px] font-bold text-slate-400 hover:text-slate-700 mb-1">
                  {showHidden ? '▲ hide' : `▼ show`} {hidden.length} hidden member{hidden.length === 1 ? '' : 's'}
                </button>
              )}
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="py-1.5 pr-2">Agent</th><th className="py-1.5 px-2">Replies</th>
                    <th className="py-1.5 px-2">Chats Handled</th><th className="py-1.5 px-2">Open</th>
                    <th className="py-1.5 px-2">Resolved</th><th className="py-1.5 pl-2">Avg Resolution</th>
                    <th className="py-1.5 pl-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAgents.map((a, i) => (
                    <tr key={`${a.name}-${i}`} className={`border-b border-slate-50 text-xs font-semibold ${hidden.includes(a.name) ? 'opacity-40' : ''} text-slate-800`} data-testid={`desk-agent-row-${i}`}>
                      <td className="py-2 pr-2 flex items-center gap-1.5 font-bold">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: AGENT_COLORS[agentNames.indexOf(a.name) % AGENT_COLORS.length] }} />
                        {a.name} <span className="text-[9px] font-semibold text-slate-400 uppercase">{a.role}</span>
                      </td>
                      <td className="py-2 px-2">{a.replies}</td>
                      <td className="py-2 px-2">{a.chats_handled}</td>
                      <td className="py-2 px-2">{a.open_chats}</td>
                      <td className="py-2 px-2 text-emerald-700">{a.resolved}</td>
                      <td className="py-2 pl-2">{a.avg_resolution_minutes != null ? `${Math.round(a.avg_resolution_minutes)}m` : '—'}</td>
                      <td className="py-2 pl-2">
                        <button data-testid={`desk-hide-toggle-${i}`} onClick={() => toggleHidden(a.name)}
                          title={hidden.includes(a.name) ? 'Show in analytics' : 'Hide from analytics'}
                          className="p-1 rounded-lg text-slate-300 hover:text-slate-700 hover:bg-slate-100">
                          {hidden.includes(a.name) ? <Eye size={13} /> : <EyeOff size={13} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.daily_chart?.length > 0 && (
            <div data-testid="desk-daily-chart">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Chats per day</p>
              <div className="flex items-end gap-1.5 h-28">
                {data.daily_chart.map((d) => {
                  const total = agentNames.reduce((s, n) => s + (d[n] || 0), 0);
                  return (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${d.day}: ${total} chats`}>
                      <div className="w-full max-w-[42px] flex flex-col-reverse rounded-t overflow-hidden" style={{ height: `${(total / maxDay) * 88}px` }}>
                        {agentNames.map((n, i) => (d[n] || 0) > 0 && (
                          <div key={n} style={{ height: `${(d[n] / (total || 1)) * 100}%`, background: AGENT_COLORS[i % AGENT_COLORS.length] }} title={`${n}: ${d[n]}`} />
                        ))}
                      </div>
                      <span className="text-[8px] font-bold text-slate-400 truncate w-full text-center">{dayLabel(d.day)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-3 flex-wrap mt-2">
                {agentNames.map((n, i) => (
                  <span key={n} className="flex items-center gap-1 text-[10px] font-bold text-slate-500">
                    <span className="w-2 h-2 rounded-full" style={{ background: AGENT_COLORS[i % AGENT_COLORS.length] }} /> {n}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
